import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import JffSyncPanel from "../components/JffSyncPanel";
import PlatformHubTabs from "../components/PlatformHubTabs";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Activity, ArrowUpRight, BarChart3, Clock3, DollarSign, ExternalLink,
  RefreshCw, Repeat2, Users, Wallet,
} from "lucide-react";

const JFF_AMBER = "#F59E0B";
const DASHBOARD_CARDS = [
  { id: "jff_new_subscribers_today", period: "Today", label: "New Subs", icon: Users, tone: "#60A5FA" },
  { id: "jff_net_sales_today", period: "Today", label: "Sales", icon: DollarSign, tone: "#34D399" },
  { id: "jff_new_subscribers_month", period: "This Month", label: "New Subs", icon: Users, tone: "#60A5FA" },
  { id: "jff_net_sales_month", period: "This Month", label: "Sales", icon: DollarSign, tone: "#34D399" },
  { id: "jff_rebilling_count", period: "This Month", label: "Rebilling", icon: Repeat2, tone: "#A78BFA" },
  { id: "jff_reported_lifetime_net_sales", period: "Lifetime", label: "Total Sales", icon: BarChart3, tone: JFF_AMBER },
  { id: "jff_active_subscribers", period: "Lifetime", label: "Active Subs", icon: Users, tone: JFF_AMBER },
  { id: "jff_pending_payout", period: "Pending", label: "Payout", icon: Wallet, tone: JFF_AMBER },
];
const SUBSCRIBER_METRICS = [
  ["jff_cancelled_subscribers_all_time", "Cancelled subscribers"],
  ["jff_free_followers", "Free followers"],
  ["jff_store_sales_all_time", "Reported store sales"],
  ["jff_one_month_only_pct", "One-month subscribers"],
  ["jff_long_term_subscriber_pct", "Long-term subscribers"],
  ["jff_avg_long_term_months", "Average long-term months"],
  ["jff_follower_to_subscriber_conversion_pct", "Follower conversion"],
  ["jff_resubscribe_rate_pct", "Resubscribe rate"],
  ["jff_interactive_subscribers_pct", "Interactive subscribers"],
  ["jff_avg_days_to_first_tip", "Average days to first tip"],
  ["jff_mass_ppv_unlock_rate", "Mass PPV unlock rate"],
  ["jff_individual_ppv_unlock_rate", "Individual PPV unlock rate"],
  ["jff_net_subscriber_change", "Net subscriber change"],
  ["jff_tips_this_month", "Tips this month"],
  ["jff_live_chat_tips_this_month", "Live chat tips this month"],
  ["jff_total_tips_count", "All-time tips"],
  ["jff_average_tip_amount", "Average tip amount"],
  ["jff_median_tip_amount", "Median tip amount"],
  ["jff_performer_percentile", "Performer percentile"],
  ["jff_performer_percentile_basis", "Percentile basis"],
  ["jff_peak_engagement_times", "Peak engagement times"],
];
const JFF_INSIGHT_LABELS = {
  jff_insight_dm_behavior: "DM rebill insight",
  jff_insight_dm_rebill_lift: "DM rebill lift",
  jff_insight_ppv_behavior: "PPV rebill insight",
  jff_insight_ppv_rebill_lift: "PPV rebill lift",
  jff_insight_promo_behavior: "Promotion rebill insight",
  jff_insight_promo_rebill_lift: "Promotion rebill lift",
  jff_insight_price_context: "Pricing analysis context",
  jff_insight_price_alert: "Pricing analysis alert",
  jff_insight_price_summary: "Pricing analysis summary",
};
const JFF_PAYOUT_METRICS = [
  ["jff_payout_all_time_earnings", "All-time paid"],
  ["jff_payout_all_time_count", "Total payouts"],
  ["jff_payout_this_year_earnings", "This year"],
  ["jff_payout_this_month_earnings", "This month"],
  ["jff_payout_share_pct", "Your share"],
];
const MASS_MESSAGE_METRICS = {
  total_mass_messages: "Mass messages",
  total_recipients: "Recipients",
  total_purchases: "Purchases",
  total_earned: "Reported earned",
};
const MASS_MESSAGE_RANGES = {
  all: "All Time",
  "3m": "Last 3 Months",
  "6m": "Last 6 Months",
  "12m": "Last 12 Months",
};

function displayMetric(metric) {
  if (!metric) return "Not synced";
  if (metric.raw_value !== null && metric.raw_value !== undefined && String(metric.raw_value).trim()) return metric.raw_value;
  if (metric.parsed_value === null || metric.parsed_value === undefined) return "Not synced";
  const value = Number(metric.parsed_value);
  if (!Number.isFinite(value)) return "Not synced";
  return metric.currency
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: metric.currency }).format(value)
    : value.toLocaleString();
}

function money(value) {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
    : "Not synced";
}

function displayMoney(value, currency) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "Not synced";
  if (!currency || !/^[A-Z]{3}$/.test(currency)) return `${Number(value).toLocaleString()} · currency unavailable`;
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(Number(value));
}

function dateLabel(value) {
  if (!value) return "No dashboard snapshot captured";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function safeJffThumbnail(value) {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || !/(^|\.)justfor\.fans$/i.test(url.hostname)) return null;
    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

function entityPayload(entity) {
  return entity?.normalized_payload || entity?.normalized || {};
}

function massMessageSnapshots(reportedMetrics) {
  const rows = Object.entries(reportedMetrics || {}).flatMap(([key, metric]) => {
    const match = key.match(/^jff_mass_message_(3m|6m|12m|all)_(total_mass_messages|total_recipients|total_purchases|total_earned)$/);
    return match ? [{ key, range: match[1], kind: match[2], metric }] : [];
  });
  const latestCapture = rows.map(({ metric }) => metric.captured_at).filter(Boolean).sort().at(-1);
  return {
    capturedAt: latestCapture,
    range: rows.find(({ metric }) => metric.captured_at === latestCapture)?.range || null,
    metrics: rows.filter(({ metric }) => metric.captured_at === latestCapture),
  };
}

export default function JustForFans() {
  const [platformStats, setPlatformStats] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [extensionPosts, setExtensionPosts] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [detailedSales, setDetailedSales] = useState([]);
  const [pricePerformance, setPricePerformance] = useState({ promo_codes: [], cancellation_reasons: [] });
  const [crmLinkedPosts, setCrmLinkedPosts] = useState([]);
  const [payoutDetails, setPayoutDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingPosts, setSyncingPosts] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    setPermissionDenied(false);
    const failures = [];
    const request = async (label, promise, fallback) => {
      try { return await promise; }
      catch (error) {
        if ([401, 403].includes(error?.response?.status)) setPermissionDenied(true);
        else failures.push(label);
        return { data: fallback };
      }
    };
    const entities = (entityType, limit = 1000) => api.get("/api/platform-data/entities", {
      params: { platform: "justforfans", entity_type: entityType, limit },
    });
    const [statsResult, ledgerResult, postsResult, sellersResult, salesResult, payoutsResult, priceResult, socialResult] = await Promise.all([
      request("Extension observations", api.get("/api/platform-data/stats/justforfans"), null),
      request("Finance ledger", api.get("/api/jff/dashboard-kpis", { params: { auto_sync: false } }), null),
      request("Captured posts", entities("jff_post"), { entities: [] }),
      request("Top Sellers", entities("jff_top_seller"), { entities: [] }),
      request("Detailed Sales", entities("jff_store_sale_item"), { entities: [] }),
      request("Payout details", entities("jff_payout_observation", 200), { entities: [] }),
      request("Price Strategy observations", api.get("/api/pricing-strategy/justforfans/reported-performance"), { promo_codes: [], cancellation_reasons: [] }),
      request("CRM-linked posts", api.get("/api/social-posts/"), []),
    ]);
    setPlatformStats(statsResult.data);
    setLedger(ledgerResult.data);
    const normalizeEntities = (response) => (response.data?.entities || []).map((entity) => ({
      ...entityPayload(entity),
      account_id: entity.account_id,
      observed_at: entity.captured_at || entity.last_seen,
    }));
    setExtensionPosts(normalizeEntities(postsResult).map((post) => ({
      post_id: post.post_id,
      caption: String(post.post_text || "").replace(/\s+/g, " ").trim().slice(0, 240),
      posted_at: post.posted_at,
      access_control: post.access_control,
      thumbnail_url: safeJffThumbnail(post.thumbnail_url),
      account_id: post.account_id,
      observed_at: post.observed_at,
    })));
    setTopSellers(normalizeEntities(sellersResult).filter((item) => item.item_title));
    setDetailedSales(normalizeEntities(salesResult).filter((item) => item.item_title));
    setPricePerformance(priceResult.data || { promo_codes: [], cancellation_reasons: [] });
    setCrmLinkedPosts((socialResult.data || []).filter((post) => post.platform === "justforfans"
      && Array.isArray(post.linked_crm_ids) && post.linked_crm_ids.length > 0));
    setPayoutDetails((payoutsResult.data?.entities || []).map((entity) => ({
      ...entityPayload(entity),
      account_id: entity.account_id,
      observed_at: entity.captured_at || entity.last_seen,
    })));
    if (failures.length) setLoadError(`Some sections could not load: ${failures.join(", ")}.`);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const reportedMetrics = platformStats?.reported_metrics || {};
  const snapshotAt = useMemo(() => Object.values(reportedMetrics)
    .map((metric) => metric.captured_at)
    .filter(Boolean)
    .sort()
    .at(-1) || platformStats?.last_synced_at || null, [reportedMetrics, platformStats?.last_synced_at]);
  const subscriberMetrics = SUBSCRIBER_METRICS
    .map(([id, label]) => ({ id, label, metric: reportedMetrics[id] }));
  const jffInsights = Object.entries(reportedMetrics)
    .filter(([id]) => JFF_INSIGHT_LABELS[id] || /^jff_pricing_tier_\d+$/.test(id))
    .map(([id, metric]) => ({ id, label: JFF_INSIGHT_LABELS[id] || "Historical pricing tier", metric }));
  const allSalesDashboardMetrics = Object.entries(reportedMetrics)
    .filter(([id]) => /^jff_sales_\d{4}_\d{1,2}_.+_(?:count|amount)$/.test(id))
    .map(([id, metric]) => ({ id, metric, period: id.match(/^jff_sales_(\d{4})_(\d{1,2})_/).slice(1) }));
  const latestSalesCapture = allSalesDashboardMetrics.map(({ metric }) => metric.captured_at)
    .filter(Boolean).sort().at(-1);
  const salesDashboardMetrics = allSalesDashboardMetrics
    .filter(({ metric }) => metric.captured_at === latestSalesCapture)
    .sort((a, b) => (a.metric.original_label || a.id).localeCompare(b.metric.original_label || b.id));
  const salesPeriod = salesDashboardMetrics[0]?.period;
  const salesPeriodLabel = salesPeriod
    ? `${salesPeriod[0]} ${salesPeriod[1] === "0" ? "All months" : `Month ${salesPeriod[1]}`}` : "";
  const payoutSummaryMetrics = JFF_PAYOUT_METRICS
    .map(([id, label]) => ({ id, label, metric: reportedMetrics[id] }));
  const payoutYear = reportedMetrics.jff_payout_year?.raw_value || "";
  const payoutMonth = reportedMetrics.jff_payout_month?.raw_value || "";
  const payoutSnapshotAt = payoutSummaryMetrics.map(({ metric }) => metric?.captured_at).filter(Boolean).sort().at(-1);
  const massMessages = massMessageSnapshots(reportedMetrics);
  const hasExtensionData = Object.keys(reportedMetrics).length > 0 || extensionPosts.length > 0
    || topSellers.length > 0 || detailedSales.length > 0 || payoutDetails.length > 0
    || pricePerformance.promo_codes.length > 0 || pricePerformance.cancellation_reasons.length > 0;

  const syncCapturedPosts = async () => {
    setSyncingPosts(true);
    try {
      const response = await api.get("/api/platform-data/entities", {
        params: { platform: "justforfans", entity_type: "jff_post", limit: 1000 },
      });
      const posts = [...new Map((response.data?.entities || []).map((entity) => {
        const payload = entityPayload(entity);
        const externalId = payload.post_id || entity.external_id || entity.entity_id;
        return [externalId, externalId ? { platform: "justforfans", external_id: String(externalId) } : null];
      }).filter(([, post]) => post)).values()];
      if (!posts.length) {
        toast.info("No captured JFF posts are available to sync yet.");
        return;
      }
      const result = await api.post("/api/social-posts/sync-extension", { posts });
      toast.success(`Post Tracker: ${result.data.created} new · ${result.data.updated} updated`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not sync captured JFF posts to Post Tracker.");
    } finally {
      setSyncingPosts(false);
    }
  };

  return (
    <AppLayout
      title="JustForFans"
      overline="CREATOR DASHBOARD · VERIFIED EXTENSION SNAPSHOTS"
      actions={(
        <div className="flex items-center gap-2">
          <a href="https://justfor.fans/stats" target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 text-[12px]">
            Detailed Stats <ExternalLink size={12} />
          </a>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost inline-flex items-center gap-2 text-[12px]">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      )}>
      <PlatformHubTabs platform="justforfans" platformName="JustForFans">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b pb-4" style={{ borderColor: "var(--gio-border)" }}>
        <div>
          <div className="overline" style={{ color: JFF_AMBER }}>JUSTFORFANS · DASHBOARD</div>
          <div className="mt-1 flex items-center gap-2 text-xs" style={{ color: "var(--gio-muted-2)" }}>
            <Clock3 size={12} /> Snapshot captured {dateLabel(snapshotAt)}
          </div>
        </div>
        <button type="button" onClick={syncCapturedPosts} disabled={syncingPosts} className="btn-ghost inline-flex items-center gap-2 text-[12px]">
          <RefreshCw size={13} className={syncingPosts ? "animate-spin" : ""} /> Sync captured posts to Tracker
        </button>
      </div>

      <JffSyncPanel />

      {loading && <p role="status" className="mb-4 border-y py-3 text-xs" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>Loading JFF extension observations…</p>}
      {permissionDenied && <p role="alert" className="mb-4 border border-red-500/40 px-3 py-2 text-xs text-red-300">Permission denied while loading one or more JFF CRM sections. Confirm your CRM access and retry.</p>}
      {loadError && <p role="alert" className="mb-4 border border-amber-500/40 px-3 py-2 text-xs text-amber-200">{loadError}</p>}
      {!loading && !hasExtensionData && <p role="status" className="mb-4 border-y py-3 text-xs" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>No JFF extension observations are synced yet. Visit a supported JFF page while signed in to capture visible data.</p>}
      {!loading && hasExtensionData && <p role="status" className="mb-4 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Extension observation · latest captured snapshot {dateLabel(snapshotAt)}</p>}

      <section aria-label="JustForFans dashboard metrics" className="mb-6 space-y-3">
          <p className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Extension observation · captured dashboard values are not ledger entries.</p>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
          {DASHBOARD_CARDS.slice(0, 5).map((card) => <MetricCard key={card.id} card={card} metric={reportedMetrics[card.id]} />)}
        </div>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
          {DASHBOARD_CARDS.slice(5).map((card) => <MetricCard key={card.id} card={card} metric={reportedMetrics[card.id]} />)}
        </div>
      </section>

      {(subscriberMetrics.length > 0 || jffInsights.length > 0) && <section className="mb-6 grid gap-6 border-y py-4 lg:grid-cols-2" style={{ borderColor: "var(--gio-border)" }} data-testid="jff-subscriber-insights">
        {subscriberMetrics.length > 0 && <div>
          <h2 className="mb-1 text-sm font-semibold" style={{ color: "var(--gio-text)" }}>Stats & Subscribers</h2>
          <p className="mb-3 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Reported platform snapshot · {dateLabel(snapshotAt)}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
            {subscriberMetrics.map(({ id, label, metric }) => <SnapshotMetric key={id} id={id} label={label} metric={metric} />)}
          </div>
        </div>}
        {jffInsights.length > 0 && <div>
          <h2 className="mb-1 text-sm font-semibold" style={{ color: "var(--gio-text)" }}>JFF insights & pricing tiers</h2>
          <p className="mb-3 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Platform-provided text captured with the dashboard snapshot; not a new recommendation.</p>
          <div className="space-y-3">
            {jffInsights.map(({ id, label, metric }) => <div key={id} className="border-l-2 pl-3" style={{ borderColor: JFF_AMBER }}>
              <div className="text-[10px] font-medium uppercase" style={{ color: "var(--gio-muted-3)" }}>{label}</div>
              <div className="mt-1 whitespace-pre-wrap text-xs" style={{ color: "var(--gio-text)" }}>{displayMetric(metric)}</div>
              <div className="mt-1 text-[9px]" style={{ color: "var(--gio-muted-3)" }}>{dateLabel(metric.captured_at)}</div>
            </div>)}
          </div>
        </div>}
      </section>}

      <section className="mb-6 border-y py-4" style={{ borderColor: "var(--gio-border)" }} data-testid="jff-sales-snapshot">
        <div className="mb-3">
          <h2 className="text-sm font-semibold" style={{ color: "var(--gio-text)" }}>Sales Dashboard snapshot</h2>
          <p className="mt-1 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Extension observation · {salesPeriodLabel || "Period not synced"} · {dateLabel(latestSalesCapture)} · excluded from Finance ledger</p>
        </div>
        {salesDashboardMetrics.length ? <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {salesDashboardMetrics.map(({ id, metric }) => <SnapshotMetric key={id} id={id} label={metric.original_label || id} metric={metric} />)}
        </div> : <EmptyState>Not synced — no Sales Dashboard summary captured.</EmptyState>}
      </section>

      <section className="mb-6 border-y py-4" style={{ borderColor: "var(--gio-border)" }} data-testid="jff-payout-snapshot">
        <div className="mb-3">
          <h2 className="text-sm font-semibold" style={{ color: "var(--gio-text)" }}>JFF payouts received</h2>
          <p className="mt-1 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Reported JFF payout · {dateLabel(payoutSnapshotAt)} · detail rows describe timing and method and are not added to these totals or the Finance ledger.</p>
        </div>
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
          {payoutSummaryMetrics.map(({ id, label, metric }) => {
            const periodLabel = id === "jff_payout_this_year_earnings" && payoutYear ? `This year (${payoutYear})`
              : id === "jff_payout_this_month_earnings" && payoutMonth ? `This month (${payoutMonth})` : label;
            return <SnapshotMetric key={id} id={id} label={periodLabel} metric={metric} />;
          })}
        </div>
        {payoutDetails.length > 0 ? <div className="border-t pt-3" style={{ borderColor: "var(--gio-border)" }}>
          <h3 className="mb-2 text-xs font-semibold" style={{ color: "var(--gio-text)" }}>Payout timing and method · captured rows only</h3>
          <div className="space-y-1">
            {payoutDetails.slice(0, 30).map((payout, index) => <div key={`${payout.account_id}:${payout.date_submitted}:${index}`} className="grid grid-cols-2 gap-2 border-b py-2 text-[11px] sm:grid-cols-6" style={{ borderColor: "var(--gio-border)" }}>
              <span className="truncate" style={{ color: "var(--gio-muted-3)" }}>{payout.account_id || "Account unavailable"}</span>
              <span style={{ color: "var(--gio-text)" }}>{payout.date_submitted || "Not synced"}</span>
              <span style={{ color: "var(--gio-muted-2)" }}>Expected {payout.expected_arrival || "Not synced"}</span>
              <span style={{ color: "var(--gio-muted-2)" }}>{payout.method || "Not synced"}</span>
              <span style={{ color: "var(--gio-text)" }}>{displayMoney(payout.reported_amount, payout.currency)}</span>
              <span style={{ color: "var(--gio-muted-3)" }}>{payout.status || "Not synced"}</span>
            </div>)}
          </div>
          <p className="mt-2 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Rows are payout details, not new earnings. The visible exchange-value column and pay-stub references are not imported.</p>
        </div> : <EmptyState>{loading ? "Loading payout details…" : "Not synced — no payout detail rows captured."}</EmptyState>}
      </section>

      <section className="mb-6 border-y py-4" style={{ borderColor: "var(--gio-border)" }} data-testid="jff-ledger-revenue">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold" style={{ color: "var(--gio-text)" }}>Finance ledger</h2>
            <p className="mt-1 text-[11px]" style={{ color: "var(--gio-muted-2)" }}>Canonical revenue entries. Dashboard sales snapshots above are not added to these totals.</p>
          </div>
          <a href="/crm/money?tab=revenue" className="inline-flex items-center gap-1 text-xs" style={{ color: JFF_AMBER }}>
            Revenue <ArrowUpRight size={13} />
          </a>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <LedgerMetric label="All-time ledger" value={ledger?.total_lifetime} />
          <LedgerMetric label="Today" value={ledger?.earnings?.today} />
          <LedgerMetric label="Yesterday" value={ledger?.earnings?.yesterday} />
          <LedgerMetric label="This month" value={ledger?.earnings?.mtd} />
          <LedgerMetric label="Last 30 days" value={ledger?.earnings?.last_30d} />
        </div>
      </section>

      <div className="mb-6 grid gap-5 lg:grid-cols-2">
        <DataSection title="Extension-captured posts" href="/crm/social?tab=tracker" linkLabel="Post Tracker">
          {extensionPosts.length ? extensionPosts.slice(0, 8).map((post, index) => (
            <div key={`${post.account_id}:${post.post_id || post.posted_at}:${index}`} className="flex gap-3 border-b py-3 last:border-b-0" style={{ borderColor: "var(--gio-border)" }}>
              {post.thumbnail_url && <img src={post.thumbnail_url} alt="" loading="lazy" className="h-14 w-20 shrink-0 object-cover" />}
              <div className="min-w-0 flex-1">
                <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{post.access_control || "Tier not synced"} · {post.posted_at || "Date not synced"}</div>
                <p className="mt-1 line-clamp-3 break-words text-xs" style={{ color: "var(--gio-text)" }}>{post.caption || "Caption not synced"}</p>
              </div>
            </div>
          )) : <EmptyState>{loading ? "Loading captured posts…" : "Not synced — no JFF posts have been captured."}</EmptyState>}
        </DataSection>

        <DataSection title="CRM 360 links" href="/crm/contacts" linkLabel="CRM 360">
          {crmLinkedPosts.length ? crmLinkedPosts.slice(0, 8).map((post, index) => (
            <div key={`${post.id}:${index}`} className="flex items-center justify-between gap-3 border-b py-2 last:border-b-0" style={{ borderColor: "var(--gio-border)" }}>
              <div className="min-w-0"><p className="line-clamp-2 text-xs" style={{ color: "var(--gio-text)" }}>{post.caption || "Caption not synced"}</p><p className="mt-1 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{post.posted_at || "Date not synced"} · CRM link</p></div>
              <span className="shrink-0 text-[10px]" style={{ color: JFF_AMBER }}>{post.linked_crm_ids.length} linked</span>
            </div>
          )) : <EmptyState>{loading ? "Loading explicit CRM links…" : "No JFF posts are explicitly linked to CRM records."}</EmptyState>}
        </DataSection>

        <DataSection title="Top Sellers · all time · Extension observation" href="/crm/money?tab=leaderboard" linkLabel="Leaderboard">
          {topSellers.length ? topSellers.slice(0, 8).map((item, index) => (
            <ItemObservation key={`${item.account_id}:${item.item_title}:${index}`} item={item} quantity={item.reported_qty_sold} amount={item.reported_revenue} />
          )) : <EmptyState>{loading ? "Loading extension report…" : "Not synced — no Top Sellers snapshot."}</EmptyState>}
        </DataSection>

        <DataSection title="Detailed Sales · visible-row sample · Extension observation" href="/crm/money?tab=leaderboard" linkLabel="Leaderboard">
          {detailedSales.length ? detailedSales.slice(0, 8).map((item, index) => (
            <ItemObservation key={`${item.account_id}:${item.item_title}:${index}`} item={item} quantity={item.reported_purchase_count} amount={item.reported_revenue} />
          )) : <EmptyState>{loading ? "Loading visible sales rows…" : "Not synced — no safe item aggregates captured."}</EmptyState>}
        </DataSection>

        <DataSection title="Price Strategy · Extension observation" href="/crm/pricing-strategy" linkLabel="Price Strategy">
          <div className="mb-4">
            <h3 className="mb-2 text-xs font-semibold" style={{ color: "var(--gio-text)" }}>Promo usage</h3>
            {pricePerformance.promo_codes.length ? pricePerformance.promo_codes.slice(0, 8).map((promo, index) => <div key={`${promo.account_id}:${promo.code}:${index}`} className="flex items-center justify-between gap-3 border-b py-2 last:border-b-0" style={{ borderColor: "var(--gio-border)" }}>
              <div className="min-w-0"><div className="truncate text-xs" style={{ color: "var(--gio-text)" }}>{promo.code || "Code not synced"}</div><div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{promo.account_id || "Account unavailable"} · {dateLabel(promo.observed_at)}</div></div>
              <span className="shrink-0 text-right text-[11px]" style={{ color: "var(--gio-muted-2)" }}>{promo.uses ?? "Not synced"} uses · {displayMoney(promo.price, promo.currency)}</span>
            </div>) : <EmptyState>{loading ? "Loading promo observations…" : "Not synced — no visible promo rows."}</EmptyState>}
          </div>
          <div className="border-t pt-3" style={{ borderColor: "var(--gio-border)" }}>
            <h3 className="mb-2 text-xs font-semibold" style={{ color: "var(--gio-text)" }}>Cancellation categories</h3>
            {pricePerformance.cancellation_reasons.length ? pricePerformance.cancellation_reasons.slice(0, 8).map((reason, index) => <div key={`${reason.account_id}:${reason.category}:${index}`} className="flex items-center justify-between gap-3 border-b py-2 last:border-b-0" style={{ borderColor: "var(--gio-border)" }}>
              <span className="min-w-0 truncate text-xs" style={{ color: "var(--gio-text)" }}>{reason.category}</span>
              <span className="shrink-0 text-[11px]" style={{ color: "var(--gio-muted-2)" }}>{reason.count ?? "Not synced"} · lost {displayMoney(reason.reported_lost_amount, reason.currency)}</span>
            </div>) : <EmptyState>{loading ? "Loading cancellation observations…" : "Not synced — no visible cancellation categories."}</EmptyState>}
            <p className="mt-2 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Freeform reasons are not stored; lost amounts are shown only when visible.</p>
          </div>
        </DataSection>

        <DataSection title={`Mass Messages · ${massMessages.range ? MASS_MESSAGE_RANGES[massMessages.range] : "Extension observation"}`}>
          {massMessages.metrics.length ? <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {massMessages.metrics.map(({ key, kind, metric }) => <SnapshotMetric key={key} id={key} label={MASS_MESSAGE_METRICS[kind]} metric={metric} />)}
            </div>
            <p className="mt-3 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Private-campaign summary · captured {dateLabel(massMessages.capturedAt)} · not added to Top Sellers, Detailed Sales, payouts, Post Tracker, or Finance.</p>
          </> : <EmptyState>{loading ? "Loading campaign summary…" : "Not synced — no Mass Message summary for a supported range."}</EmptyState>}
        </DataSection>
      </div>

      <DataSection title="JFF shared workflows">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <ActionLink href="/crm/money?tab=revenue" icon={<DollarSign size={14} />}>Revenue</ActionLink>
          <ActionLink href="/crm/money?tab=leaderboard" icon={<BarChart3 size={14} />}>Leaderboard</ActionLink>
          <ActionLink href="/crm/social?tab=tracker" icon={<Activity size={14} />}>Post Tracker</ActionLink>
          <ActionLink href="/crm/contacts" icon={<Users size={14} />}>CRM 360</ActionLink>
        </div>
        <p className="mt-3 text-[11px]" style={{ color: "var(--gio-muted-2)" }}>Only explicit Post Tracker CRM links appear in the CRM 360 section; public-post mentions are not auto-linked.</p>
      </DataSection>
      </PlatformHubTabs>
    </AppLayout>
  );
}

function MetricCard({ card, metric }) {
  const Icon = card.icon;
  return (
    <div className="min-w-0 border p-3" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }} data-testid={`jff-metric-${card.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-medium uppercase" style={{ color: "var(--gio-muted-2)" }}>{card.period}</span>
        <Icon size={14} style={{ color: card.tone }} />
      </div>
      <div className="mt-2 truncate text-xl font-bold" style={{ color: metric ? "var(--gio-text)" : "var(--gio-muted-3)" }}>
        {displayMetric(metric)}
      </div>
      <div className="mt-1 truncate text-xs" style={{ color: "var(--gio-muted-2)" }}>{card.label}</div>
    </div>
  );
}

function SnapshotMetric({ id, label, metric }) {
  return <div className="min-w-0 border-l-2 pl-3" style={{ borderColor: JFF_AMBER }} data-testid={`jff-subscriber-metric-${id}`}>
    <div className="text-[10px] uppercase" style={{ color: "var(--gio-muted-3)" }}>{label}</div>
    <div className="mt-1 truncate text-sm font-semibold" style={{ color: "var(--gio-text)" }}>{displayMetric(metric)}</div>
  </div>;
}

function ItemObservation({ item, quantity, amount }) {
  const period = item.first_sale_at || item.last_sale_at
    ? `${item.first_sale_at || "Date unavailable"} – ${item.last_sale_at || "Date unavailable"}`
    : `Captured ${item.observed_at || "time unavailable"}`;
  return <div className="flex items-center justify-between gap-3 border-b py-2 last:border-b-0" style={{ borderColor: "var(--gio-border)" }}>
    <div className="min-w-0"><p className="truncate text-xs" style={{ color: "var(--gio-text)" }}>{item.item_title || "Item not synced"}</p><p className="truncate text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{item.account_id || "Account unavailable"} · {period}</p></div>
    <span className="shrink-0 text-right text-[11px]" style={{ color: "var(--gio-muted-2)" }}>{quantity ?? "Not synced"} sold · {displayMoney(amount, item.currency)}</span>
  </div>;
}

function LedgerMetric({ label, value }) {
  return (
    <div className="border-l-2 pl-3" style={{ borderColor: JFF_AMBER }}>
      <div className="text-[10px] uppercase" style={{ color: "var(--gio-muted-3)" }}>{label}</div>
      <div className="mt-1 text-lg font-semibold" style={{ color: "var(--gio-text)" }}>{value === undefined || value === null ? "Not synced" : money(value)}</div>
    </div>
  );
}

function DataSection({ title, href, linkLabel, children }) {
  return (
    <section className="min-w-0 border-t pt-3" style={{ borderColor: "var(--gio-border)" }}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold" style={{ color: "var(--gio-text)" }}>{title}</h2>
        {href && <a href={href} className="inline-flex min-h-11 shrink-0 items-center gap-1 px-2 text-[11px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]" style={{ color: JFF_AMBER }}>{linkLabel}<ArrowUpRight size={12} /></a>}
      </div>
      {children}
    </section>
  );
}

function ActionLink({ href, icon, children }) {
  return <a href={href} className="inline-flex min-h-11 min-w-0 items-center gap-2 border px-3 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]" style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }}>{icon}<span className="truncate">{children}</span></a>;
}

function EmptyState({ children }) {
  return <p className="py-3 text-xs" style={{ color: "var(--gio-muted-3)" }}>{children}</p>;
}